import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent as RKeyboardEvent, type PointerEvent as RPointerEvent, type ReactNode, type TouchEvent as RTouchEvent } from "react";
import { useParams, useSearchParams } from "react-router";
import {
  ArrowUp, Bell, BellRing, Bookmark, BookmarkCheck, Check, ChevronUp, Clapperboard, ExternalLink, EyeOff, Flag, Gauge, ListVideo,
  Loader2, Maximize2, MessageCircle, Minimize2, MoreHorizontal, PanelRightClose, PanelRightOpen, Pause, Pin, Play, RotateCcw, RotateCw,
  Scissors, Send, Sun, SunDim, ThumbsUp, TriangleAlert, Users, VolumeX, X,
} from "lucide-react";
import {
  P, PortalShell, usePortal, useNow, PLink, Btn, IconBtn, Chip, SessionChip, Card, List, Row, SectionHead, Segmented,
  Avatar, Name, Thumb, ProgressBar, CopyCode, TierLock, Gate, JoinButton, lockLine, CountdownBoxes, LocalTime, AddToCalendar,
  Hand, Empty, useChecklist,
  getSession, projectOf, sessionState, sessionsFor, sessionEnd, qaFor, fmtT, fmtDur, fmtTime, fmtDay, fmtLeft, timeLabel, relDay, who, can, sameZone,
  type Session, type Project, type Chapter, type FixNote, type SessionState,
} from "./Portal";
import "./Session.css";

/* P3 · Class night / replay (/blue-2/portal/session/:id).

   One screen for every state of a club Session:
   - pre (upcoming / tonight): waiting-room hero with countdown, add to calendar,
     "Can't make it? We'll save your spot", and the What-you-need checklist.
   - live: the player at the live edge (with a little DVR: ±15/30s, Back to live),
     LIVE badge + viewer count, live chat (sent locally), reactions, a questions
     queue, "Can't stay? Save my spot". Chelsea gets moderation (pin / hide /
     mute for 5 min, 15 min or the rest of class) and "Mark chapter".
   - processing: "Replay is processing, ready in about an hour" + notify me,
     with the chapters and chat from the night already browsable.
   - replay: chapters (chips + scrubber ticks), fix-note markers that toast when
     playback reaches them, ±15/30s, speed, keep-awake, fullscreen, chat replay
     synced to the video.
   Non-members get a 30-second preview and a tier-named lock.

   Phone: sticky player + big controls, chapter chips under it, and a dock at
   the bottom (swipe up) that opens a drawer under the player with Chat /
   Questions / Chapters / What you need. Desktop: player + a 380px right panel
   with the same tabs; the panel starts collapsed when there's no chat.

   The video is a 44-second stand-in clip that loops; the timeline, chapters and
   fix notes are the real class length. Link to a moment with ?at=1:12:40 (or
   #t=4360). Everything is local state; nothing is sent. */

const CLIP = 44;
const PREVIEW = 30;
const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];
const REACTIONS = ["🧵", "❤️", "👏", "😂", "🎉"];

type Mode = "pre" | "live" | "processing" | "replay";
type PanelTab = "chat" | "questions" | "chapters" | "supplies";
type Line = { id: string; t: number; who: string; text: string; q?: boolean; sys?: boolean };
type LiveQ = { id: string; t: number; who: string; text: string; votes: number; voted?: boolean; answered?: boolean };

const modeOf = (st: SessionState, s: Session): Mode =>
  st === "upcoming" || st === "tonight" ? "pre" : st === "replay" && !s.replay ? "processing" : st;

/** "1:12:40" / "4360" / "72m" → seconds. */
function parseT(v: string | null | undefined): number | null {
  if (!v) return null;
  if (/^\d+(\.\d+)?$/.test(v)) return Number(v);
  if (/^\d+(:\d{1,2}){1,2}$/.test(v)) return v.split(":").map(Number).reduce((a, n) => a * 60 + n, 0);
  return null;
}
const chapterAt = (chs: Chapter[], t: number) => { let c: Chapter | undefined; for (const x of chs) if (x.t <= t + 0.5) c = x; return c; };

/* ═══════════════════════════ Route ═══════════════════════════ */

export function PortalSession() {
  const { id } = useParams();
  const { clock } = usePortal();
  const s = getSession(id);
  if (!s) return <PortalShell active="classes" back={{ to: P.classes, label: "Classes" }} title="Class not found">
    <Empty icon={<Clapperboard size={22} aria-hidden="true" />} title="We couldn't find that class" body="It may have moved. Every class and replay lives under Classes." hand="hmm…" action={<Btn to={P.classes}>Go to Classes</Btn>} />
  </PortalShell>;
  if (s.kind === "youtube") return <YouTubeSession s={s} />;
  return <ClassSession key={`${s.id}-${clock}`} s={s} />;
}

/* ═══════════════════════════ Small hooks ═══════════════════════════ */

function useWide() {
  const q = "(min-width: 900px)";
  const [w, setW] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q), f = () => setW(m.matches);
    m.addEventListener("change", f); return () => m.removeEventListener("change", f);
  }, []);
  return w;
}

function usePop<T extends HTMLElement>() {
  const [open, setOpen] = useState(false);
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", off); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", off); document.removeEventListener("keydown", esc); };
  }, [open]);
  return { open, setOpen, ref };
}

/** Simulated playback over the real class length (the <video> is a looping stand-in). */
function usePlayback({ length, initial, live, edge, limit, fixNotes, autoplay }: {
  length: number; initial: number; live: boolean; edge: number; limit?: number; fixNotes: FixNote[]; autoplay: boolean;
}) {
  const [pos, setPosState] = useState(initial);
  const [playing, setPlaying] = useState(autoplay);
  const [speed, setSpeed] = useState(1);
  const [toast, setToast] = useState<FixNote | null>(null);
  const [limitHit, setLimitHit] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const posRef = useRef(initial);
  const o = useRef({ length, live, edge, limit, fixNotes, speed });
  o.current = { length, live, edge, limit, fixNotes, speed };
  const cap = () => Math.min(o.current.limit ?? Infinity, o.current.live ? o.current.edge : o.current.length);
  const setPos = (v: number) => { posRef.current = v; setPosState(v); };

  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const id = window.setInterval(() => {
      const t = performance.now(), dt = (t - last) / 1000; last = t;
      const prev = posRef.current, m = cap();
      let next = prev + dt * o.current.speed;
      if (next >= m) {
        next = m;
        if (o.current.limit != null && m >= o.current.limit) { setPlaying(false); setLimitHit(true); }
        else if (!o.current.live) setPlaying(false);
      }
      const hit = o.current.fixNotes.find(f => prev < f.t && f.t <= next);
      if (hit) setToast(hit);
      setPos(next);
    }, 250);
    return () => window.clearInterval(id);
  }, [playing]);

  useEffect(() => {
    const v = videoRef.current; if (!v) return;
    v.playbackRate = speed;
    if (playing) v.play().catch(() => { /* autoplay may be blocked; the timeline still runs */ }); else v.pause();
  }, [playing, speed]);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 10000);
    return () => window.clearTimeout(id);
  }, [toast]);

  const seek = useCallback((t: number, play?: boolean) => {
    const v = Math.max(0, Math.min(t, cap()));
    setPos(v);
    const vid = videoRef.current; if (vid) try { vid.currentTime = v % CLIP; } catch { /* not loaded yet */ }
    if (play) setPlaying(true);
  }, []);
  const toggle = useCallback(() => {
    if (!playing && !o.current.live && posRef.current >= o.current.length - 1) seek(0);
    setPlaying(p => !p);
  }, [playing, seek]);
  return { pos, playing, setPlaying, speed, setSpeed, seek, skip: (d: number) => seek(posRef.current + d), toggle, toast, setToast, limitHit, setLimitHit, videoRef };
}
type PB = ReturnType<typeof usePlayback>;

/* Live room: chat (seeded from the night's chat, plus local sends and a trickle
   of sample messages), questions queue, reactions, viewer count, moderation. */
const PIN_SEED: Line = { id: "pin0", t: 125, who: "chelsea", text: "Template + bias tape maker next to the machine, please! SPELLBOUND25 works through Thursday 🧙‍♀️" };
const FILLERS: [string, string][] = [
  ["dee", "My cone is lopsided but I'm committed"], ["maggie", "Cosmo cameo!! 🐈‍⬛"], ["tasha", "Went back 30 seconds, got it now"],
  ["linh", "What stitch length are you using?"], ["bev", "This brim is SO cute"], ["ruthie", "Hi from Provo 👋"],
  ["carmen", "Clips > pins for this foam"], ["noor", "Does the iron go on cotton setting?"], ["jill", "Got it!!"], ["paula", "Love this method"],
];
const Q_SEED: LiveQ[] = [
  { id: "lq1", t: 19 * 60 + 2, who: "carmen", text: "Do we cut the brim on the fold?", votes: 4, answered: true },
  { id: "lq2", t: 31 * 60 + 10, who: "paula", text: "Bias strips: 2\" or 2½\" for the 1\" tape maker?", votes: 6 },
  { id: "lq3", t: 42 * 60 + 15, who: "jill", text: "My interfacing is bubbling. Press longer?", votes: 2, answered: true },
  { id: "lq4", t: 44 * 60 + 30, who: "noor", text: "Can I skip the batting in the top if my fabric is heavy?", votes: 3 },
];

function useRoom(s: Session, mode: Mode, edge: number, role: string) {
  const seed = s.replay?.chatReplay ?? [];
  const [extra, setExtra] = useState<Line[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [muted, setMuted] = useState<Record<string, number>>({});
  const [pinned, setPinned] = useState<string | null>(mode === "live" ? "pin0" : null);
  const [qs, setQs] = useState<LiveQ[]>(Q_SEED);
  const [viewers, setViewers] = useState(23);
  const [floats, setFloats] = useState<{ id: number; e: string; x: number }[]>([]);
  const edgeRef = useRef(edge); edgeRef.current = edge;
  const mutedRef = useRef(muted); mutedRef.current = muted;
  const n = useRef(0);

  const float = useCallback((e: string) => {
    const id = ++n.current;
    setFloats(f => [...f.slice(-14), { id, e, x: 8 + Math.random() * 30 }]);
    window.setTimeout(() => setFloats(f => f.filter(x => x.id !== id)), 2400);
  }, []);

  useEffect(() => {
    if (mode !== "live") return;
    let i = 0, alive = true, tid = 0;
    const tick = () => {
      if (!alive) return;
      const [w, text] = FILLERS[i++ % FILLERS.length];
      const until = mutedRef.current[w];
      if (!(until && until > edgeRef.current)) setExtra(x => [...x, { id: `f${++n.current}`, t: edgeRef.current, who: w, text }]);
      if (Math.random() < 0.5) float(REACTIONS[Math.floor(Math.random() * REACTIONS.length)]);
      setViewers(v => Math.max(19, Math.min(28, v + (Math.random() < 0.5 ? -1 : 1))));
      tid = window.setTimeout(tick, 7000 + Math.random() * 6000);
    };
    tid = window.setTimeout(tick, 4000);
    return () => { alive = false; window.clearTimeout(tid); };
  }, [mode, float]);

  const lines: Line[] = useMemo(() => {
    const base = seed.map((l, i) => ({ id: `s${i}`, ...l }));
    const all = mode === "live" ? [...base.filter(l => l.t <= edge), ...(edge >= PIN_SEED.t ? [PIN_SEED] : []), ...extra] : base;
    return all.sort((a, b) => a.t - b.t);
  }, [seed, mode, Math.floor(edge), extra]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = (text: string, asQ: boolean) => {
    const me = role === "chelsea" ? "chelsea" : "me";
    const t = edgeRef.current;
    setExtra(x => [...x, { id: `m${++n.current}`, t, who: me, text, q: asQ }]);
    if (asQ) setQs(q => [...q, { id: `q${n.current}`, t, who: me, text, votes: 1, voted: true }]);
  };
  const mute = (w: string, mins: number | null) => {
    const until = mins == null ? Infinity : edgeRef.current + mins * 60;
    setMuted(m => ({ ...m, [w]: until }));
    setExtra(x => [...x, { id: `sys${++n.current}`, t: edgeRef.current, who: w, sys: true, text: mins == null ? "muted for the rest of class" : `muted for ${mins} min` }]);
  };
  const unmute = (w: string, sysId: string) => {
    setMuted(m => { const c = { ...m }; delete c[w]; return c; });
    setExtra(x => x.filter(l => l.id !== sysId));
  };
  const toggleHide = (id: string) => setHidden(h => { const c = new Set(h); if (c.has(id)) c.delete(id); else c.add(id); return c; });
  const vote = (id: string) => setQs(q => q.map(x => x.id === id ? { ...x, voted: !x.voted, votes: x.votes + (x.voted ? -1 : 1) } : x));
  const answer = (id: string) => setQs(q => q.map(x => x.id === id ? { ...x, answered: !x.answered } : x));
  const visibleQs = qs.filter(q => q.t <= edge || q.id.startsWith("q"));
  return { lines, hidden, muted, pinned, setPinned, qs: visibleQs, viewers, floats, float, send, mute, unmute, toggleHide, vote, answer };
}
type Room = ReturnType<typeof useRoom>;

/* ═══════════════════════════ The class screen ═══════════════════════════ */

function ClassSession({ s }: { s: Session }) {
  const { role, tz } = usePortal();
  const now = useNow(1000);
  const wide = useWide();
  const [sp] = useSearchParams();
  const p = projectOf(s)!;
  const state = sessionState(s, now);
  const mode = modeOf(state, s);
  const member = mode === "live" ? can(role, "live") : can(role, "replays");
  const chelsea = role === "chelsea";
  const live = mode === "live";
  const planned = s.minutes * 60;
  const edge = live ? Math.max(0, Math.min((now.getTime() - s.start.getTime()) / 1000, planned)) : 0;
  const length = live ? planned : s.replay?.length ?? planned;

  // Chelsea's local marks (chapters / fix notes added in this visit).
  const [addedCh, setAddedCh] = useState<Chapter[]>([]);
  const [addedFix, setAddedFix] = useState<FixNote[]>([]);
  const chapters = useMemo(() => [...(s.replay?.chapters ?? []).filter(c => !live || c.t <= edge), ...addedCh].sort((a, b) => a.t - b.t),
    [s, live, Math.floor(edge / 5), addedCh]); // eslint-disable-line react-hooks/exhaustive-deps
  const fixNotes = useMemo(() => [...(mode !== "replay" ? [] : s.replay?.fixNotes ?? []), ...addedFix].sort((a, b) => a.t - b.t), [s, mode, addedFix]);

  const atParam = parseT(sp.get("at")) ?? parseT(typeof window !== "undefined" ? window.location.hash.match(/t=([\d:.]+)/)?.[1] : null);
  const resumeFrom = s.progress && s.replay && s.progress < s.replay.length - 30 ? s.progress : 0;
  const [previewing, setPreviewing] = useState(false);
  const pb = usePlayback({
    length, live, edge, fixNotes,
    initial: live ? edge : !member ? 0 : atParam ?? resumeFrom,
    limit: member ? undefined : PREVIEW,
    autoplay: live && member,
  });
  const room = useRoom(s, mode, edge, role);
  const hasChat = live || room.lines.length > 0;

  // Panels: desktop right panel / phone drawer share the tab.
  const tabs: { value: PanelTab; label: ReactNode }[] = live
    ? [{ value: "chat", label: "Chat" }, { value: "questions", label: <>Questions</> }, { value: "chapters", label: "Chapters" }, { value: "supplies", label: "Supplies" }]
    : [{ value: "chapters", label: "Chapters" }, ...(hasChat ? [{ value: "chat" as PanelTab, label: mode === "processing" ? "Chat" : "Chat replay" }] : []), { value: "supplies", label: "Supplies" }];
  const [tab, setTab] = useState<PanelTab>(live ? "chat" : "chapters");
  const [panelOpen, setPanelOpen] = useState(hasChat && member);
  const [drawer, setDrawer] = useState(false);
  const openDrawer = (t: PanelTab) => { setTab(t); setDrawer(true); };

  // Unread chat count for the phone dock.
  const chatVisible = tab === "chat" && (wide ? panelOpen : drawer);
  const [seen, setSeen] = useState(room.lines.length);
  useEffect(() => { if (chatVisible) setSeen(room.lines.length); }, [chatVisible, room.lines.length]);
  const unread = chatVisible ? 0 : Math.max(0, room.lines.length - seen);

  // Small status notices on the player ("Screen will stay on", "Chapter marked").
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => { if (!notice) return; const id = window.setTimeout(() => setNotice(null), 3200); return () => window.clearTimeout(id); }, [notice]);

  // Keyboard: space/k play, ←/→ 15s, shift+←/→ 30s.
  useEffect(() => {
    if (mode !== "replay" && mode !== "live") return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.metaKey || e.ctrlKey || e.altKey || el.closest("input, textarea, select, [contenteditable], [role=slider], [role=menu]")) return;
      if (!member && !previewing) return;
      if (e.key === " " || e.key === "k") { if (el.closest("button, a")) return; e.preventDefault(); pb.toggle(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); pb.skip(e.shiftKey ? -30 : -15); }
      else if (e.key === "ArrowRight") { e.preventDefault(); pb.skip(e.shiftKey ? 30 : 15); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, member, previewing, pb]);

  // Measure the sticky stage so the phone drawer opens right under it.
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageH, setStageH] = useState(0);
  useEffect(() => {
    const el = stageRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setStageH(el.getBoundingClientRect().height));
    ro.observe(el); return () => ro.disconnect();
  }, [mode]);

  const startPreview = () => { pb.setLimitHit(false); setPreviewing(true); pb.seek(0, true); };
  const playerMode = mode === "live" || mode === "replay";

  const back = { to: P.project(p.id), label: p.title };
  const panelBody = (t: PanelTab) => t === "chat"
    ? <ChatView s={s} mode={mode} room={room} pb={pb} member={member} chelsea={chelsea} />
    : t === "questions" ? <QuestionsView room={room} member={member} chelsea={chelsea} />
    : t === "chapters" ? <ChaptersView chapters={chapters} fixNotes={fixNotes} pb={pb} live={live} edge={edge} member={member || previewing} mode={mode} />
    : <WhatYouNeed p={p} />;

  if (mode === "pre") return <PortalShell active="classes" back={back} className="ss-page ss-is-pre">
    <div className="ss ss-mode-pre">
      <div className="ss-main">
        <WaitCard s={s} p={p} state={state} member={member} />
        <div className="ss-info">
          <SaveSpot kind="pre" s={s} member={member} />
          {s.blurb && <section className="ss-sec"><SectionHead title="In this class" /><p className="ss-blurb">{s.blurb}</p>
            {p.about.length > 1 && <Card tone="paper" pad="md" className="ss-before"><p className="pt-kicker">Before class</p><p>{p.about[p.about.length - 1].replace(/^Before class:\s*(.)/i, (_, c: string) => c.toUpperCase())}</p></Card>}</section>}
          <MoreFromProject p={p} current={s} now={now} />
        </div>
      </div>
      <aside className="ss-panel ss-panel-static" aria-label="What you need">
        <div className="ss-panel-head"><h2>What you need</h2></div>
        <div className="ss-panel-body">{panelBody("supplies")}</div>
      </aside>
    </div>
  </PortalShell>;

  const showPanel = wide && panelOpen;
  return <PortalShell active="classes" back={back} focus hideLive={live} className={`ss-page ss-is-${mode}`}>
    <div className={`ss ss-mode-${mode}${showPanel ? "" : " no-panel"}`}>
      <div className="ss-main">
        <div className={`ss-stage${playerMode ? " is-sticky" : ""}`} ref={stageRef}>
          {playerMode
            ? <Player s={s} pb={pb} mode={mode} length={length} edge={edge} chapters={chapters} fixNotes={fixNotes}
                member={member} previewing={previewing} onPreview={startPreview} room={room} chelsea={chelsea}
                notice={notice} setNotice={setNotice} wide={wide} panelOpen={panelOpen} setPanelOpen={setPanelOpen}
                onMark={(kind, label) => {
                  const t = Math.round(live ? edge : pb.pos);
                  if (kind === "chapter") setAddedCh(c => [...c, { t, label }]); else setAddedFix(f => [...f, { t, text: label }]);
                  setNotice(`${kind === "chapter" ? "Chapter" : "Fix note"} added at ${fmtT(t)}`);
                }} />
            : <ProcessingStage s={s} now={now} member={member} />}
        </div>
        {playerMode && chapters.length > 0 && (member || previewing) && <ChapterChips chapters={chapters} fixNotes={fixNotes} pb={pb} live={live} />}

        <div className="ss-info">
          <InfoHead s={s} p={p} state={state} mode={mode} tz={tz} length={length} edge={edge} room={room} />
          {!member && <div className="ss-phone-only">
            <TierLock thing={live ? "live" : "replays"} length={live ? undefined : length} preview={!live}
              title={pb.limitHit ? "That's the 30-second preview" : undefined} onPreview={live ? undefined : startPreview} />
          </div>}
          {mode !== "live" && fixNotes.length > 0 && <FixNotesCallout fixNotes={fixNotes} pb={pb} member={member} mode={mode} />}
          {live && <SaveSpot kind="live" s={s} member={member} at={pb.pos} />}
          {wide && !panelOpen && <div className="ss-below">
            <section className="ss-sec"><SectionHead title="Chapters" action={hasChat ? <Btn variant="ghost" size="sm" icon={<PanelRightOpen size={16} aria-hidden="true" />} onClick={() => setPanelOpen(true)}>Show panel</Btn> : undefined} />{panelBody("chapters")}</section>
            <section className="ss-sec"><SectionHead title="What you need" /><Card pad="md">{panelBody("supplies")}</Card></section>
          </div>}
          {mode === "replay" && <ReplayQuestions s={s} p={p} pb={pb} now={now} member={member} />}
          <MoreFromProject p={p} current={s} now={now} />
          <p className="ss-proto">Prototype: the picture is a 44-second stand-in clip on a loop. The timeline, chapters and fix notes are the real class length ({fmtDur(length)}). Chat and questions stay on this device.</p>
        </div>
      </div>

      {showPanel && <aside className="ss-panel" aria-label="Class panel">
        <div className="ss-panel-head">
          <Segmented size="sm" block value={tab} onChange={setTab} options={tabs.map(t => ({ ...t, count: t.value === "questions" ? room.qs.filter(q => !q.answered).length : undefined }))} label="Class panel" />
          <IconBtn label="Hide panel" icon={<PanelRightClose size={18} />} size={36} onClick={() => setPanelOpen(false)} />
        </div>
        <div className={`ss-panel-body is-${tab}`}>{panelBody(tab)}</div>
      </aside>}
    </div>

    {!wide && <Dock mode={mode} tab={tab} unread={unread} qCount={room.qs.filter(q => !q.answered).length} p={p} hasChat={hasChat} onOpen={openDrawer} drawerOpen={drawer} />}
    {!wide && drawer && <Drawer top={playerMode ? stageH : null} tab={tab} setTab={setTab} tabs={tabs} onClose={() => setDrawer(false)}>{panelBody(tab)}</Drawer>}
  </PortalShell>;
}

/* ═══════════════════════════ Player ═══════════════════════════ */

function Player({ s, pb, mode, length, edge, chapters, fixNotes, member, previewing, onPreview, room, chelsea, notice, setNotice, wide, panelOpen, setPanelOpen, onMark }: {
  s: Session; pb: PB; mode: Mode; length: number; edge: number; chapters: Chapter[]; fixNotes: FixNote[]; member: boolean; previewing: boolean; onPreview: () => void;
  room: Room; chelsea: boolean; notice: string | null; setNotice: (n: string) => void; wide: boolean; panelOpen: boolean; setPanelOpen: (b: boolean) => void;
  onMark: (kind: "chapter" | "fix", label: string) => void;
}) {
  const live = mode === "live";
  const wrapRef = useRef<HTMLDivElement>(null);
  const [theater, setTheater] = useState(false);
  const [fs, setFs] = useState(false);
  const [awake, setAwake] = useState(false);
  const wake = useRef<{ release?: () => Promise<void> } | null>(null);
  const locked = !member && (!previewing || pb.limitHit);
  const behind = live ? edge - pb.pos : 0;
  const atLive = behind < 3;

  useEffect(() => {
    const f = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", f);
    return () => { document.removeEventListener("fullscreenchange", f); wake.current?.release?.().catch(() => {}); };
  }, []);
  useEffect(() => {
    if (!theater) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setTheater(false); };
    document.addEventListener("keydown", esc); return () => document.removeEventListener("keydown", esc);
  }, [theater]);

  const toggleFs = async () => {
    if (document.fullscreenElement) { await document.exitFullscreen().catch(() => {}); return; }
    if (theater) { setTheater(false); return; }
    const el = wrapRef.current;
    if (el?.requestFullscreen) {
      try {
        await el.requestFullscreen();
        try { await (screen.orientation as unknown as { lock?: (o: string) => Promise<void> }).lock?.("landscape"); } catch { /* not supported */ }
        return;
      } catch { /* fall through to theater mode */ }
    }
    setTheater(true);
  };
  const toggleAwake = async () => {
    if (awake) { wake.current?.release?.().catch(() => {}); wake.current = null; setAwake(false); setNotice("Screen can sleep again"); return; }
    try { wake.current = await (navigator as unknown as { wakeLock?: { request: (t: string) => Promise<{ release: () => Promise<void> }> } }).wakeLock?.request("screen") ?? null; } catch { /* prototype */ }
    setAwake(true); setNotice("Screen stays on while you sew");
  };

  const ch = chapterAt(chapters, pb.pos);
  const controlsOff = locked;
  return <div className={`ss-player${theater ? " is-theater" : ""}${fs ? " is-fs" : ""}`} ref={wrapRef}>
    <div className="ss-screen">
      <video ref={pb.videoRef} src="/video/live-class.mp4" poster={s.replay?.poster ?? "/video/live-class.jpg"} muted loop playsInline preload="metadata" aria-label={`${s.title} video`}
        onClick={() => !controlsOff && pb.toggle()} />
      <div className="ss-badges">
        {live && <Chip tone="live">Live</Chip>}
        {live && <span className="ss-viewers"><Users size={14} aria-hidden="true" />{room.viewers} watching</span>}
        {!live && previewing && !pb.limitHit && <span className="ss-viewers">Preview · {fmtT(Math.max(0, PREVIEW - pb.pos))} left</span>}
      </div>
      {!pb.playing && !controlsOff && <button type="button" className="ss-bigplay" onClick={pb.toggle} aria-label={pb.pos > 1 && !live ? `Resume at ${fmtT(pb.pos)}` : "Play"}>
        <Play size={26} fill="currentColor" aria-hidden="true" />{!live && <span>{pb.pos > 5 ? `Resume at ${fmtT(pb.pos)}` : "Play"}</span>}
      </button>}
      {live && member && <div className="ss-floats" aria-hidden="true">{room.floats.map(f => <span key={f.id} style={{ right: `${f.x}%` } as CSSProperties}>{f.e}</span>)}</div>}
      {notice && <div className="ss-notice" role="status">{notice}</div>}
      {pb.toast && <div className="ss-toast" role="status">
        <TriangleAlert size={18} aria-hidden="true" />
        <div><b>Fix note · {fmtT(pb.toast.t)}</b><p>{pb.toast.text}</p></div>
        <IconBtn label="Dismiss fix note" icon={<X size={16} />} size={32} onClick={() => pb.setToast(null)} />
      </div>}
      {locked && <>
        <div className="ss-lock-phone">
          <span className="ss-lock-ic"><Scissors size={16} aria-hidden="true" /></span>
          <p><b>{pb.limitHit ? "That's the preview" : live ? "Class is live now" : "Members-only replay"}</b><span>{lockLine("member", live ? undefined : length, !live)}</span></p>
          {!live && !pb.limitHit && <Btn variant="pop" size="sm" icon={<Play size={14} aria-hidden="true" />} onClick={onPreview}>Watch 30s preview</Btn>}
          {(live || pb.limitHit) && <JoinButton need="member" size="sm" variant="pop" />}
        </div>
        <TierLock className="ss-lock-desk" variant="overlay" thing={live ? "live" : "replays"} length={live ? undefined : length} preview={!live}
          title={pb.limitHit ? "That's the 30-second preview" : live ? `${room.viewers} people are sewing along right now` : undefined} onPreview={live || pb.limitHit ? undefined : onPreview} />
      </>}
    </div>

    <div className={`ss-ctl${controlsOff ? " is-off" : ""}`}>
      <div className="ss-ctl-scrub">
        <span className="ss-time">{fmtT(pb.pos)}</span>
        <Scrubber length={length} pos={pb.pos} edge={live ? edge : undefined} limit={member ? undefined : PREVIEW} chapters={chapters} fixNotes={fixNotes} onSeek={t => pb.seek(t)} disabled={controlsOff} />
        {live
          ? <button type="button" className={`ss-golive${atLive ? " is-on" : ""}`} onClick={() => pb.seek(edge, true)} disabled={controlsOff} aria-label={atLive ? "At the live edge" : `Back to live (${fmtT(behind)} behind)`}>
              <i className="pt-dot" aria-hidden="true" />{atLive ? "Live" : `−${fmtT(behind)}`}
            </button>
          : <span className="ss-time">{fmtT(length)}</span>}
      </div>
      <div className="ss-ctl-row">
        <div className="ss-ctl-main">
          <button type="button" className="ss-skip" onClick={() => pb.skip(-30)} disabled={controlsOff} aria-label="Back 30 seconds"><RotateCcw size={20} aria-hidden="true" /><b>30</b></button>
          <button type="button" className="ss-skip" onClick={() => pb.skip(-15)} disabled={controlsOff} aria-label="Back 15 seconds"><RotateCcw size={20} aria-hidden="true" /><b>15</b></button>
          <button type="button" className="ss-play" onClick={pb.toggle} disabled={controlsOff} aria-label={pb.playing ? "Pause" : "Play"}>
            {pb.playing ? <Pause size={24} fill="currentColor" aria-hidden="true" /> : <Play size={24} fill="currentColor" aria-hidden="true" />}
          </button>
          <button type="button" className="ss-skip" onClick={() => pb.skip(15)} disabled={controlsOff || (live && atLive)} aria-label="Forward 15 seconds"><RotateCw size={20} aria-hidden="true" /><b>15</b></button>
          <button type="button" className="ss-skip" onClick={() => pb.skip(30)} disabled={controlsOff || (live && atLive)} aria-label="Forward 30 seconds"><RotateCw size={20} aria-hidden="true" /><b>30</b></button>
          {wide && <span className="ss-ctl-ch">{ch ? ch.label : ""}</span>}
        </div>
        <div className="ss-ctl-extra">
          {chelsea && <MarkMenu at={live ? edge : pb.pos} live={live} upcoming={(s.replay?.chapters ?? []).filter(c => c.t > edge).map(c => c.label).slice(0, 3)} onMark={onMark} />}
          <SpeedMenu speed={pb.speed} setSpeed={pb.setSpeed} disabled={controlsOff || (live && atLive)} />
          <button type="button" className={`ss-ic${awake ? " is-on" : ""}`} onClick={toggleAwake} aria-pressed={awake} aria-label="Keep screen awake" title="Keep screen awake">
            {awake ? <Sun size={18} aria-hidden="true" /> : <SunDim size={18} aria-hidden="true" />}<span className="ss-ic-l">{awake ? "Awake" : "Keep awake"}</span>
          </button>
          {wide && !theater && !fs && <button type="button" className="ss-ic" onClick={() => setPanelOpen(!panelOpen)} aria-pressed={panelOpen} aria-label={panelOpen ? "Hide side panel" : "Show side panel"} title={panelOpen ? "Hide side panel" : "Show side panel"}>
            {panelOpen ? <PanelRightClose size={18} aria-hidden="true" /> : <PanelRightOpen size={18} aria-hidden="true" />}
          </button>}
          <button type="button" className="ss-ic" onClick={toggleFs} aria-label={fs || theater ? "Exit full screen" : "Full screen"} title={fs || theater ? "Exit full screen" : "Full screen (turn your phone sideways)"}>
            {fs || theater ? <Minimize2 size={18} aria-hidden="true" /> : <Maximize2 size={18} aria-hidden="true" />}
          </button>
        </div>
      </div>
    </div>
  </div>;
}

function Scrubber({ length, pos, edge, limit, chapters, fixNotes, onSeek, disabled }: {
  length: number; pos: number; edge?: number; limit?: number; chapters: Chapter[]; fixNotes: FixNote[]; onSeek: (t: number) => void; disabled?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.min(limit ?? Infinity, edge ?? length);
  const at = (e: RPointerEvent) => { const r = ref.current!.getBoundingClientRect(); return Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)) * length; };
  const pct = (t: number) => `${(Math.max(0, Math.min(t, length)) / length) * 100}%`;
  const shown = drag ?? pos;
  const tip = drag ?? hover;
  const tipCh = tip != null ? chapterAt(chapters, tip) : undefined;
  const onKey = (e: RKeyboardEvent) => {
    const d = e.key === "ArrowLeft" ? -15 : e.key === "ArrowRight" ? 15 : e.key === "PageDown" ? -60 : e.key === "PageUp" ? 60 : null;
    if (d != null) { e.preventDefault(); e.stopPropagation(); onSeek(pos + d); }
    else if (e.key === "Home") { e.preventDefault(); onSeek(0); }
    else if (e.key === "End") { e.preventDefault(); onSeek(max); }
  };
  return <div className={`ss-scrub${disabled ? " is-off" : ""}${drag != null ? " is-drag" : ""}`} ref={ref} role="slider" tabIndex={disabled ? -1 : 0}
    aria-label="Seek" aria-valuemin={0} aria-valuemax={Math.round(length)} aria-valuenow={Math.round(pos)} aria-disabled={disabled || undefined}
    aria-valuetext={`${fmtT(pos)} of ${fmtT(length)}${chapterAt(chapters, pos) ? `, ${chapterAt(chapters, pos)!.label}` : ""}`}
    onPointerDown={e => { if (disabled) return; ref.current!.setPointerCapture(e.pointerId); setDrag(Math.min(at(e), max)); }}
    onPointerMove={e => { const t = at(e); if (drag != null) setDrag(Math.min(t, max)); else if (e.pointerType === "mouse") setHover(t); }}
    onPointerUp={() => { if (drag != null) { onSeek(drag); setDrag(null); } }}
    onPointerCancel={() => setDrag(null)}
    onPointerLeave={() => setHover(null)}
    onKeyDown={onKey}>
    <div className="ss-scrub-track">
      {edge != null && <i className="ss-scrub-edge" style={{ width: pct(edge) }} />}
      {limit != null && <i className="ss-scrub-locked" style={{ left: pct(limit) }} />}
      <i className={`ss-scrub-fill${edge != null ? " is-live" : ""}`} style={{ width: pct(shown) }} />
      {chapters.filter(c => c.t > 0).map(c => <i key={`${c.t}-${c.label}`} className="ss-scrub-tick" style={{ left: pct(c.t) }} />)}
    </div>
    {fixNotes.map(f => <i key={f.t} className="ss-scrub-fix" style={{ left: pct(f.t) }} title={`Fix note at ${fmtT(f.t)}`} />)}
    <i className="ss-scrub-thumb" style={{ left: pct(shown) }} />
    {tip != null && <span className="ss-scrub-tip" style={{ left: `clamp(48px, ${pct(tip)}, calc(100% - 48px))` }}><b>{fmtT(Math.min(tip, length))}</b>{tipCh && <span>{tipCh.label}</span>}</span>}
  </div>;
}

function SpeedMenu({ speed, setSpeed, disabled }: { speed: number; setSpeed: (n: number) => void; disabled?: boolean }) {
  const pop = usePop<HTMLDivElement>();
  return <div className="ss-pop" ref={pop.ref}>
    <button type="button" className="ss-ic ss-speed" onClick={() => pop.setOpen(o => !o)} aria-haspopup="menu" aria-expanded={pop.open} aria-label={`Playback speed ${speed}×`} disabled={disabled} title="Playback speed">
      <Gauge size={18} aria-hidden="true" /><span>{speed}×</span>
    </button>
    {pop.open && <div className="ss-menu" role="menu" aria-label="Playback speed">
      <p className="ss-menu-h">Speed</p>
      {SPEEDS.map(x => <button key={x} type="button" role="menuitemradio" aria-checked={x === speed} className={x === speed ? "is-on" : ""} onClick={() => { setSpeed(x); pop.setOpen(false); }}>
        {x === 1 ? "Normal" : `${x}×`}{x === speed && <Check size={16} aria-hidden="true" />}
      </button>)}
    </div>}
  </div>;
}

function MarkMenu({ at, live, upcoming, onMark }: { at: number; live: boolean; upcoming: string[]; onMark: (kind: "chapter" | "fix", label: string) => void }) {
  const pop = usePop<HTMLDivElement>();
  const [kind, setKind] = useState<"chapter" | "fix">("chapter");
  const [label, setLabel] = useState("");
  const submit = (l = label) => { if (!l.trim()) return; onMark(kind, l.trim()); setLabel(""); pop.setOpen(false); };
  return <div className="ss-pop" ref={pop.ref}>
    <button type="button" className="ss-ic ss-mark" onClick={() => pop.setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={pop.open} title={live ? "Mark a chapter now" : "Add a chapter or fix note here"}>
      <Flag size={17} aria-hidden="true" /><span className="ss-ic-l">{live ? "Mark chapter" : "Add mark"}</span>
    </button>
    {pop.open && <div className="ss-menu ss-markmenu" role="dialog" aria-label="Mark this moment">
      <p className="ss-menu-h">{live ? "Mark chapter now" : "Add at this moment"} · {fmtT(at)}</p>
      {!live && <Segmented size="sm" block value={kind} onChange={setKind} options={[{ value: "chapter", label: "Chapter" }, { value: "fix", label: "Fix note" }]} label="Mark type" />}
      <form onSubmit={e => { e.preventDefault(); submit(); }} className="ss-markform">
        <input autoFocus value={label} onChange={e => setLabel(e.target.value)} maxLength={kind === "fix" ? 200 : 40} placeholder={kind === "fix" ? "What to do instead…" : "Chapter name"} aria-label={kind === "fix" ? "Fix note text" : "Chapter name"} />
        <Btn type="submit" size="sm" disabled={!label.trim()}>Add</Btn>
      </form>
      {kind === "chapter" && upcoming.length > 0 && <div className="ss-marksugg">{upcoming.map(u => <button key={u} type="button" onClick={() => submit(u)}>{u}</button>)}</div>}
      <p className="ss-menu-note">{live ? "Members see it on the scrubber right away, and it carries over to the replay." : "Saved to the replay for everyone."}</p>
    </div>}
  </div>;
}

function ChapterChips({ chapters, fixNotes, pb, live }: { chapters: Chapter[]; fixNotes: FixNote[]; pb: PB; live: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const cur = chapterAt(chapters, pb.pos);
  const idx = cur ? chapters.indexOf(cur) : -1;
  const first = useRef(true);
  useEffect(() => {
    const go = (smooth: boolean) => {
      const box = ref.current, el = box?.children[idx] as HTMLElement | undefined;
      if (box && el) box.scrollTo({ left: el.offsetLeft - 16, behavior: smooth ? "smooth" : "auto" });
    };
    go(!first.current);
    // Widths change once the web fonts land; line the chip up again then.
    if (first.current) document.fonts?.ready.then(() => go(false)).catch(() => {});
    first.current = false;
  }, [idx]);
  return <div className="ss-chips" ref={ref} role="list" aria-label="Chapters">
    {chapters.map((c, i) => {
      const next = chapters[i + 1]?.t ?? Infinity;
      const fix = fixNotes.some(f => f.t >= c.t && f.t < next);
      return <button key={`${c.t}-${c.label}`} type="button" role="listitem" className={i === idx ? "is-on" : ""} aria-current={i === idx || undefined} onClick={() => pb.seek(c.t, true)}>
        <span className="ss-chip-t">{fmtT(c.t)}</span>{c.label}{fix && <TriangleAlert size={13} className="ss-chip-fix" aria-label="Has a fix note" />}
      </button>;
    })}
    {live && <span className="ss-chips-live" role="listitem"><i className="pt-dot" aria-hidden="true" />More as Chelsea marks them</span>}
  </div>;
}

/* ═══════════════════════════ Stages for pre / processing ═══════════════════════════ */

function WaitCard({ s, p, state, member }: { s: Session; p: Project; state: SessionState; member: boolean }) {
  const doors = new Date(s.start.getTime() - 15 * 6e4);
  return <Card tone="teal" pad="lg" tape="yellow" className="ss-wait">
    <img className="ss-wait-bg" src={p.hero} alt="" />
    <div className="ss-wait-in">
      <div className="ss-wait-top">
        <p className="pt-kicker">{state === "tonight" ? "Class tonight" : "Next class"} · {p.title}</p>
        {state === "tonight" && <Hand>see you at 7!</Hand>}
      </div>
      <h1 className="ss-wait-title">{s.title}</h1>
      <LocalTime date={s.start} relative className="ss-wait-time" />
      <CountdownBoxes to={s.start} tone="onDark" />
      <div className="ss-wait-act">
        {member ? <>
          <AddToCalendar session={s} onDark />
          <p className="ss-wait-note">Doors open at {fmtTime(doors)} MT. The chat opens then, and this page turns into the class.</p>
        </> : <>
          <JoinButton need="member" variant="pop" />
          <p className="ss-wait-note">{lockLine("member")} · live classes, replays and the chat. The schedule is free to see.</p>
          <AddToCalendar session={s} onDark variant="ghost" size="sm" />
        </>}
      </div>
    </div>
  </Card>;
}

function ProcessingStage({ s, now, member }: { s: Session; now: Date; member: boolean }) {
  const { tz } = usePortal();
  const [notify, setNotify] = useState(false);
  const ready = new Date(sessionEnd(s).getTime() + 90 * 6e4);
  const mins = Math.max(5, Math.round((ready.getTime() - now.getTime()) / 6e4));
  const eta = mins >= 45 && mins <= 90 ? "about 1 hour" : mins > 90 ? `about ${Math.round(mins / 60)} hours` : `about ${mins} min`;
  return <div className="ss-proc">
    <img src={s.replay?.poster ?? "/video/live-class.jpg"} alt="" />
    <div className="ss-proc-in">
      <Chip tone="peri" icon={<Loader2 size={13} className="ss-spin" aria-hidden="true" />}>Replay processing</Chip>
      <p className="ss-proc-title">Tonight's replay is almost ready</p>
      <p className="ss-proc-meta">Ready in {eta} · by about {timeLabel(ready, tz)}</p>
      {member
        ? <Btn variant={notify ? "secondary" : "pop"} size="md" onDark={notify} aria-pressed={notify} icon={notify ? <BellRing size={17} aria-hidden="true" /> : <Bell size={17} aria-hidden="true" />} onClick={() => setNotify(n => !n)}>
            {notify ? "We'll let you know" : "Notify me when it's ready"}
          </Btn>
        : <JoinButton need="member" variant="pop" size="sm" />}
    </div>
  </div>;
}

/* ═══════════════════════════ Info under the player ═══════════════════════════ */

function InfoHead({ s, p, state, mode, tz, length, edge, room }: { s: Session; p: Project; state: SessionState; mode: Mode; tz: string; length: number; edge: number; room: Room }) {
  return <header className="ss-head">
    <PLink to={P.project(p.id)} className="ss-kicker">{p.monthLabel} · {p.title}</PLink>
    <h1 className="ss-title">{s.label === "Prep" ? `${p.title} · Prep video` : s.title}</h1>
    <div className="ss-meta">
      <SessionChip state={state} />
      <span>{fmtDay(s.start, tz)} · {timeLabel(s.start, tz)}</span>
      {mode === "live" ? <span>{fmtT(edge)} in · planned {fmtDur(length)}</span> : <span>{fmtDur(length)}</span>}
      {mode !== "live" && s.replay?.chatters ? <span>{s.replay.chatters} sewed along live</span> : null}
      {mode === "live" && <span>{room.viewers} watching</span>}
    </div>
    {mode !== "pre" && s.blurb && <p className="ss-blurb">{s.blurb}</p>}
  </header>;
}

function FixNotesCallout({ fixNotes, pb, member, mode }: { fixNotes: FixNote[]; pb: PB; member: boolean; mode: Mode }) {
  return <section className="ss-fixes" aria-labelledby="ss-fixes-h">
    <p className="ss-fixes-h" id="ss-fixes-h"><TriangleAlert size={17} aria-hidden="true" />Read before you start · {fixNotes.length} fix {fixNotes.length === 1 ? "note" : "notes"}</p>
    <ul>
      {fixNotes.map(f => <li key={f.t}>
        {member && mode === "replay"
          ? <button type="button" className="ss-fix-t" onClick={() => pb.seek(f.t - 10, true)} aria-label={`Jump to ${fmtT(f.t)}`}>{fmtT(f.t)}</button>
          : <span className="ss-fix-t">{fmtT(f.t)}</span>}
        <span>{f.text}</span>
      </li>)}
    </ul>
    {member && mode === "replay" && <p className="ss-fixes-note">You'll also get a reminder on the video when you reach each one.</p>}
  </section>;
}

function SaveSpot({ kind, s, member, at = 0 }: { kind: "pre" | "live"; s: Session; member: boolean; at?: number }) {
  const [saved, setSaved] = useState<number | null>(null);
  if (!member) return null;
  const pre = kind === "pre";
  return <Card pad="md" className={`ss-spot${saved != null ? " is-on" : ""}`}>
    <span className="ss-spot-ic">{saved != null ? <BookmarkCheck size={20} aria-hidden="true" /> : <Bookmark size={20} aria-hidden="true" />}</span>
    <div className="ss-spot-main">
      {saved == null ? <>
        <p className="ss-spot-t">{pre ? "Can't make it? We'll save your spot." : "Can't stay? We'll save your spot."}</p>
        <p className="ss-spot-b">{pre
          ? "We'll send an email and a push when the replay is up, usually within an hour of class, with chapters and fix notes."
          : `Your replay will start at ${fmtT(at)}, and we'll send a push when it's up (usually within an hour).`}</p>
      </> : <>
        <p className="ss-spot-t">{pre ? "You're on the list" : `Spot saved at ${fmtT(saved)}`}</p>
        <p className="ss-spot-b">{pre ? `We'll let you know when the ${s.label} replay is ready.` : "We'll send a push when the replay is ready, and it'll pick up right here."}</p>
      </>}
    </div>
    {saved == null
      ? <Btn variant="secondary" size="sm" onClick={() => setSaved(Math.round(at))}>{pre ? "Remind me" : "Save my spot"}</Btn>
      : <Btn variant="ghost" size="sm" onClick={() => setSaved(null)}>Undo</Btn>}
  </Card>;
}

function ReplayQuestions({ s, p, pb, now, member }: { s: Session; p: Project; pb: PB; now: Date; member: boolean }) {
  const base = qaFor(p.id, now).filter(q => q.sessionId === s.id);
  const [mine, setMine] = useState<{ t: number; text: string }[]>([]);
  const [asking, setAsking] = useState<number | null>(null);
  const [text, setText] = useState("");
  return <section className="ss-sec" aria-labelledby="ss-q-h">
    <SectionHead id="ss-q-h" title="Questions about this class" action={{ label: `All ${p.title} Q&A`, to: `${P.project(p.id)}#qa` }} />
    {base.length + mine.length > 0 && <List>
      {base.map(q => <li key={q.id} className="pt-li"><div className="ss-q">
        <Avatar person={q.by} size={32} />
        <div className="ss-q-main">
          <p className="ss-q-meta"><Name person={q.by} />{q.t != null && member && <button type="button" className="ss-tlink" onClick={() => pb.seek(q.t!, true)}>at {fmtT(q.t)}</button>}</p>
          <p>{q.text}</p>
          {q.answer ? <p className="ss-q-ans"><Name person="chelsea" /> {q.answer.text}</p> : <p className="ss-q-wait">Waiting for Chelsea · {q.votes} want to know</p>}
        </div>
      </div></li>)}
      {mine.map((q, i) => <li key={i} className="pt-li"><div className="ss-q">
        <Avatar person="me" size={32} />
        <div className="ss-q-main">
          <p className="ss-q-meta"><Name person="me" /><button type="button" className="ss-tlink" onClick={() => pb.seek(q.t, true)}>at {fmtT(q.t)}</button></p>
          <p>{q.text}</p>
          <p className="ss-q-wait">Posted. Chelsea gets a ping.</p>
        </div>
      </div></li>)}
    </List>}
    {member
      ? asking == null
        ? <div><Btn variant="secondary" size="sm" icon={<MessageCircle size={16} aria-hidden="true" />} onClick={() => setAsking(Math.round(pb.pos))}>Ask about {fmtT(pb.pos)}</Btn></div>
        : <form className="ss-ask" onSubmit={e => { e.preventDefault(); if (!text.trim()) return; setMine(m => [...m, { t: asking, text: text.trim() }]); setText(""); setAsking(null); }}>
            <label htmlFor="ss-ask-in">Your question about {fmtT(asking)}</label>
            <textarea id="ss-ask-in" autoFocus rows={2} maxLength={400} value={text} onChange={e => setText(e.target.value)} placeholder="e.g. Do the brim darts face up or down?" />
            <div className="ss-ask-act"><Btn type="submit" size="sm" disabled={!text.trim()}>Post question</Btn><Btn variant="ghost" size="sm" onClick={() => setAsking(null)}>Cancel</Btn></div>
          </form>
      : <TierLock thing="qaAsk" variant="inline" />}
  </section>;
}

function MoreFromProject({ p, current, now }: { p: Project; current: Session; now: Date }) {
  const { tz, can: canDo } = usePortal();
  const list = sessionsFor(p.id);
  if (list.length < 2) return null;
  return <section className="ss-sec" aria-labelledby="ss-more-h">
    <SectionHead id="ss-more-h" title={`${p.title} classes`} action={{ label: "Project page", to: P.project(p.id) }} />
    <List>
      {list.map(x => {
        const st = sessionState(x, now);
        const rep = st === "replay" && x.replay;
        const prog = canDo("replays") ? x.progress : 0;
        const meta = rep ? `${fmtDur(x.replay!.length)}${prog ? ` · ${prog >= x.replay!.length - 30 ? "Watched" : fmtLeft(x.replay!.length, prog)}` : ""}`
          : st === "processing" ? "Replay ready in about an hour" : `${relDay(x.start, now, tz)} · ${fmtTime(x.start, tz)}${sameZone(x.start, tz) ? " MT" : " your time"}`;
        return <Row key={x.id} active={x.id === current.id} to={x.id === current.id ? undefined : P.session(x.id)}
          lead={<Thumb src={x.replay?.poster ?? p.hero} width={88} play={!!rep} locked={!!rep && !canDo("replays")} progress={rep && prog ? prog / x.replay!.length : undefined} />}
          title={<>{x.label}{x.id === current.id && <span className="ss-here"> · You're here</span>}</>} meta={meta} trail={x.id !== current.id && (st === "live" || st === "tonight" || st === "processing") ? <SessionChip state={st} /> : undefined} />;
      })}
    </List>
  </section>;
}

/* ═══════════════════════════ Panel bodies ═══════════════════════════ */

function ChatView({ s, mode, room, pb, member, chelsea }: { s: Session; mode: Mode; room: Room; pb: PB; member: boolean; chelsea: boolean }) {
  const { tz } = usePortal();
  const live = mode === "live";
  const [sync, setSync] = useState<"sync" | "all">(mode === "replay" ? "sync" : "all");
  const listRef = useRef<HTMLOListElement>(null);
  const lines = room.lines.filter(l => (chelsea || !room.hidden.has(l.id)) && (!l.sys || chelsea) && (mode !== "replay" || sync === "all" || l.t <= pb.pos));
  const pin = room.pinned ? room.lines.find(l => l.id === room.pinned) : undefined;
  const stick = useRef(true);
  useEffect(() => {
    const el = listRef.current; if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [lines.length]);
  if (!member) return <TierLock thing={live ? "live" : "chatReplay"} title={live ? `${room.viewers} people are chatting with Chelsea right now` : "The chat from class"} />;
  const total = room.lines.filter(l => !l.sys).length;
  return <div className="ss-chat">
    {mode === "replay" && <div className="ss-chat-bar">
      <span>{sync === "sync" ? `Synced to the video · ${lines.length} of ${total}` : `${total} messages from the night`}</span>
      <Segmented size="sm" value={sync} onChange={setSync} options={[{ value: "sync", label: "Synced" }, { value: "all", label: "All" }]} label="Chat replay view" />
    </div>}
    {mode === "processing" && <div className="ss-chat-bar"><span>Chat from tonight · read-only</span></div>}
    {pin && <div className="ss-chat-pin"><Pin size={14} aria-hidden="true" /><div><b>{who(pin.who).name}</b> {pin.text}</div>
      {chelsea && <IconBtn label="Unpin" icon={<X size={14} />} size={28} onClick={() => room.setPinned(null)} />}</div>}
    <ol className="ss-chat-list" ref={listRef} aria-live={live ? "polite" : "off"} onScroll={e => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60; }}>
      {lines.length === 0 && <li className="ss-chat-empty">{mode === "replay" ? `The chat starts at ${fmtT(room.lines[0]?.t ?? 0)}. Press play and it'll follow along.` : "No messages yet."}</li>}
      {lines.map(l => {
        if (l.sys) return <li key={l.id} className="ss-chat-sys"><VolumeX size={14} aria-hidden="true" /><span><b>{who(l.who).name}</b> {l.text}</span>
          <button type="button" className="ss-tlink" onClick={() => room.unmute(l.who, l.id)}>Undo</button></li>;
        const hid = room.hidden.has(l.id);
        return <li key={l.id} className={`ss-line${hid ? " is-hidden" : ""}${l.q ? " is-q" : ""}${l.who === "chelsea" ? " is-chelsea" : ""}`}>
          <Avatar person={l.who} size={28} />
          <div className="ss-line-main">
            <p className="ss-line-meta"><Name person={l.who} />
              {mode === "replay" ? <button type="button" className="ss-tlink" onClick={() => pb.seek(l.t, true)}>{fmtT(l.t)}</button>
                : <span>{live ? fmtTime(new Date(s.start.getTime() + l.t * 1000), tz) : fmtT(l.t)}</span>}
              {l.q && <Chip tone="gold">Question</Chip>}
            </p>
            <p className="ss-line-text">{hid ? <em>Hidden from the chat</em> : l.text}</p>
          </div>
          {chelsea && live && <ModMenu line={l} room={room} />}
        </li>;
      })}
    </ol>
    {live && <Composer room={room} chelsea={chelsea} />}
  </div>;
}

function ModMenu({ line, room }: { line: Line; room: Room }) {
  const pop = usePop<HTMLDivElement>();
  const own = line.who === "chelsea";
  const act = (f: () => void) => { f(); pop.setOpen(false); };
  // The chat list scrolls (overflow), so the menu is fixed to the viewport, flips
  // upward near the bottom, and follows its button when the list scrolls.
  const [at, setAt] = useState<CSSProperties>({});
  const place = useCallback(() => {
    const el = pop.ref.current; if (!el) return;
    const r = el.getBoundingClientRect(), H = 230;
    const right = Math.max(8, window.innerWidth - r.right);
    setAt(r.bottom + H + 8 > window.innerHeight
      ? { position: "fixed", top: "auto", bottom: window.innerHeight - r.top + 2, right }
      : { position: "fixed", top: r.bottom + 2, bottom: "auto", right });
  }, [pop.ref]);
  const toggle = () => { if (!pop.open) place(); pop.setOpen(o => !o); };
  useEffect(() => {
    if (!pop.open) return;
    window.addEventListener("scroll", place, true); window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [pop.open, place]);
  return <div className="ss-pop ss-mod" ref={pop.ref}>
    <IconBtn label={`Moderate message from ${who(line.who).name}`} icon={<MoreHorizontal size={16} />} size={32} onClick={toggle} />
    {pop.open && <div className="ss-menu ss-modmenu" role="menu" style={at}>
      <button type="button" role="menuitem" onClick={() => act(() => room.setPinned(room.pinned === line.id ? null : line.id))}><Pin size={15} aria-hidden="true" />{room.pinned === line.id ? "Unpin" : "Pin to top"}</button>
      {!own && <>
        <button type="button" role="menuitem" onClick={() => act(() => room.toggleHide(line.id))}><EyeOff size={15} aria-hidden="true" />{room.hidden.has(line.id) ? "Show message" : "Hide message"}</button>
        <p className="ss-menu-h">Mute {who(line.who).name}</p>
        <button type="button" role="menuitem" onClick={() => act(() => room.mute(line.who, 5))}><VolumeX size={15} aria-hidden="true" />For 5 minutes</button>
        <button type="button" role="menuitem" onClick={() => act(() => room.mute(line.who, 15))}><VolumeX size={15} aria-hidden="true" />For 15 minutes</button>
        <button type="button" role="menuitem" onClick={() => act(() => room.mute(line.who, null))}><VolumeX size={15} aria-hidden="true" />For the rest of class</button>
      </>}
    </div>}
  </div>;
}

function Composer({ room, chelsea }: { room: Room; chelsea: boolean }) {
  const [text, setText] = useState("");
  const [asQ, setAsQ] = useState(false);
  const send = () => { const t = text.trim(); if (!t) return; room.send(t, asQ && !chelsea); setText(""); setAsQ(false); };
  return <div className="ss-compose">
    <div className="ss-react" role="group" aria-label="Send a reaction">
      {REACTIONS.map(e => <button key={e} type="button" onClick={() => room.float(e)} aria-label={`React ${e}`}>{e}</button>)}
      {!chelsea && <button type="button" className={`ss-asq${asQ ? " is-on" : ""}`} aria-pressed={asQ} onClick={() => setAsQ(q => !q)}>{asQ ? <Check size={14} aria-hidden="true" /> : null}Ask as a question</button>}
    </div>
    <form className="ss-compose-row" onSubmit={e => { e.preventDefault(); send(); }}>
      <input value={text} onChange={e => setText(e.target.value)} maxLength={280} placeholder={chelsea ? "Message as Chelsea" : asQ ? "Ask Chelsea a question…" : "Say something to the class…"} aria-label="Chat message" />
      <IconBtn label="Send" icon={<Send size={18} />} size={44} onClick={send} className="ss-send" />
    </form>
  </div>;
}

function QuestionsView({ room, member, chelsea }: { room: Room; member: boolean; chelsea: boolean }) {
  const [text, setText] = useState("");
  if (!member) return <TierLock thing="live" title="Ask Chelsea questions live" />;
  const open = room.qs.filter(q => !q.answered).sort((a, b) => b.votes - a.votes);
  const done = room.qs.filter(q => q.answered);
  const item = (q: LiveQ) => <li key={q.id} className={`ss-lq${q.answered ? " is-done" : ""}`}>
    <button type="button" className={`ss-vote${q.voted ? " is-on" : ""}`} onClick={() => room.vote(q.id)} aria-pressed={!!q.voted} aria-label={`${q.voted ? "Remove vote" : "Me too"}: ${q.votes} votes`} disabled={q.answered}>
      <ArrowUp size={15} aria-hidden="true" /><b>{q.votes}</b>
    </button>
    <div className="ss-lq-main">
      <p className="ss-lq-text">{q.text}</p>
      <p className="ss-lq-meta"><Name person={q.who} badge={false} /> · {fmtT(q.t)}{q.answered && <> · <span className="ss-ok"><Check size={13} aria-hidden="true" /> Answered on stream</span></>}</p>
    </div>
    {chelsea && <Btn variant={q.answered ? "ghost" : "secondary"} size="sm" onClick={() => room.answer(q.id)}>{q.answered ? "Reopen" : "Answered"}</Btn>}
  </li>;
  return <div className="ss-qs">
    <p className="ss-qs-note">{chelsea ? "Most-wanted first. Mark them as you answer on stream." : "Tap ↑ on questions you want answered too. Chelsea works through the top ones."}</p>
    {open.length ? <ol className="ss-lqs">{open.map(item)}</ol> : <p className="ss-chat-empty">All caught up!</p>}
    {done.length > 0 && <><p className="ss-menu-h ss-qs-h">Answered</p><ol className="ss-lqs">{done.map(item)}</ol></>}
    {!chelsea && <form className="ss-compose-row ss-qs-form" onSubmit={e => { e.preventDefault(); if (!text.trim()) return; room.send(text.trim(), true); setText(""); }}>
      <input value={text} onChange={e => setText(e.target.value)} maxLength={280} placeholder="Ask Chelsea a question…" aria-label="Your question" />
      <IconBtn label="Ask" icon={<Send size={18} />} size={44} onClick={() => { if (text.trim()) { room.send(text.trim(), true); setText(""); } }} className="ss-send" />
    </form>}
  </div>;
}

function ChaptersView({ chapters, fixNotes, pb, live, edge, member, mode }: { chapters: Chapter[]; fixNotes: FixNote[]; pb: PB; live: boolean; edge: number; member: boolean; mode: Mode }) {
  const cur = chapterAt(chapters, pb.pos);
  if (!chapters.length) return <Empty icon={<ListVideo size={22} aria-hidden="true" />} title="No chapters yet" body={live ? "Chelsea marks chapters as she goes. They'll show up here and on the scrubber." : "Chapters are added when the replay is posted."} />;
  const canSeek = member && (mode === "replay" || mode === "live");
  return <div className="ss-chaps">
    {live && <p className="ss-qs-note">Marked live by Chelsea. Tap one to rewind to it.</p>}
    {mode === "processing" && <p className="ss-qs-note">Marked during class. They'll be clickable when the replay is up.</p>}
    <List flush label="Chapters">
      {chapters.map((c, i) => {
        const next = chapters[i + 1]?.t ?? (live ? edge : Infinity);
        const fx = fixNotes.filter(f => f.t >= c.t && f.t < next);
        return <Row key={`${c.t}-${c.label}`} active={canSeek && c === cur} onClick={canSeek ? () => pb.seek(c.t, true) : undefined} chevron={false}
          lead={<span className="ss-chap-t">{fmtT(c.t)}</span>} title={c.label}
          meta={fx.length ? <span className="ss-chap-fix"><TriangleAlert size={13} aria-hidden="true" />Fix note at {fx.map(f => fmtT(f.t)).join(", ")}</span> : undefined}
          trail={canSeek && c === cur ? <Chip tone="teal">{pb.playing ? "Playing" : "Here"}</Chip> : undefined} />;
      })}
    </List>
  </div>;
}

function WhatYouNeed({ p }: { p: Project }) {
  const ck = useChecklist(p.id);
  const [list, setList] = useState<"pattern" | "chelseaUsed">("pattern");
  const items = p.supplies[list];
  const c = ck.count(p.supplies.pattern);
  const has = p.supplies.pattern.length + p.supplies.chelseaUsed.length > 0;
  return <Gate thing="supplies" lock={{ title: "See what you need for class", body: "The supply list and pattern link are free with an account. The discount code comes with the Member plan." }}>
    <div className="ss-need">
      <div className="ss-need-pat">
        <p className="pt-kicker">Pattern</p>
        <p className="ss-need-name">{p.pattern.name}</p>
        <p className="ss-need-by">{p.pattern.designer} · {p.pattern.price}</p>
        {p.pattern.code && <CopyCode code={p.pattern.code} discount={p.pattern.discount} expires={p.pattern.codeExpires} block />}
        {p.pattern.url !== "#" && <Btn href={p.pattern.url} variant="secondary" size="sm" iconEnd={<ExternalLink size={14} aria-hidden="true" />}>Get the pattern</Btn>}
      </div>
      {has ? <>
        <div className="ss-need-count"><p><b>{c.done} of {c.total}</b> gathered{c.done === c.total && " · you're ready!"}</p><ProgressBar value={c.total ? c.done / c.total : 0} label="Supplies gathered" /></div>
        <Segmented size="sm" block value={list} onChange={setList} label="Supply list"
          options={[{ value: "pattern", label: "Pattern list", count: p.supplies.pattern.length }, { value: "chelseaUsed", label: "Chelsea used", count: p.supplies.chelseaUsed.length }]} />
        {items.length ? <ul className="ss-check">
          {items.map(i => <li key={i.id}><label className={ck.has(i.id) ? "is-on" : ""}>
            <input type="checkbox" checked={ck.has(i.id)} onChange={() => ck.toggle(i.id)} />
            <span className="ss-check-box" aria-hidden="true"><Check size={14} strokeWidth={3} /></span>
            <span className="ss-check-main">
              <span className="ss-check-name">{i.name}{i.optional && <span className="ss-opt">optional</span>}</span>
              {(i.qty || i.note) && <span className="ss-check-meta">{[i.qty, i.note].filter(Boolean).join(" · ")}</span>}
            </span>
          </label></li>)}
        </ul> : <p className="ss-qs-note">Chelsea used the pattern list as written.</p>}
      </> : <Empty title="Supply list coming soon" body="Chelsea posts it about a week before Class 1." />}
    </div>
  </Gate>;
}

/* ═══════════════════════════ Phone dock & drawer ═══════════════════════════ */

function Dock({ mode, tab, unread, qCount, p, hasChat, onOpen, drawerOpen }: {
  mode: Mode; tab: PanelTab; unread: number; qCount: number; p: Project; hasChat: boolean; onOpen: (t: PanelTab) => void; drawerOpen: boolean;
}) {
  const { can: canDo } = usePortal();
  const ck = useChecklist(p.id);
  const c = ck.count(p.supplies.pattern);
  const y = useRef<number | null>(null);
  const live = mode === "live";
  const onStart = (e: RTouchEvent) => { y.current = e.touches[0].clientY; };
  const onEnd = (e: RTouchEvent) => {
    if (y.current != null && y.current - e.changedTouches[0].clientY > 30) onOpen(hasChat ? (live || tab === "chat" ? "chat" : tab) : tab);
    y.current = null;
  };
  const btn = (t: PanelTab, icon: ReactNode, label: string, badge?: ReactNode) =>
    <button type="button" className={`ss-dock-btn${drawerOpen && tab === t ? " is-on" : ""}`} onClick={() => onOpen(t)}>{icon}<span>{label}</span>{badge}</button>;
  return <div className="ss-dock" onTouchStart={onStart} onTouchEnd={onEnd}>
    <span className="ss-dock-grip" aria-hidden="true"><ChevronUp size={16} /></span>
    <div className="ss-dock-row">
      {hasChat && btn("chat", <MessageCircle size={20} aria-hidden="true" />, live ? "Chat" : mode === "processing" ? "Chat" : "Chat replay", unread > 0 ? <b className="ss-badge">{unread > 99 ? "99+" : unread}</b> : undefined)}
      {live ? btn("questions", <ThumbsUp size={20} aria-hidden="true" />, "Questions", qCount ? <b className="ss-badge is-soft">{qCount}</b> : undefined)
        : btn("chapters", <ListVideo size={20} aria-hidden="true" />, "Chapters")}
      {btn("supplies", <Scissors size={20} aria-hidden="true" />, "Supplies", c.total && canDo("supplies") ? <b className="ss-badge is-soft">{c.done}/{c.total}</b> : undefined)}
    </div>
  </div>;
}

function Drawer({ top, tab, setTab, tabs, onClose, children }: {
  top: number | null; tab: PanelTab; setTab: (t: PanelTab) => void; tabs: { value: PanelTab; label: ReactNode }[]; onClose: () => void; children: ReactNode;
}) {
  const y = useRef<number | null>(null);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc); return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  const style = (top != null ? { top: `calc(var(--pt-top-h) + var(--pt-live-h) + ${Math.round(top)}px)` } : undefined) as CSSProperties | undefined;
  return <div className={`ss-drawer${top == null ? " is-tall" : ""}`} style={style} role="dialog" aria-label="Class panel">
    <div className="ss-drawer-head" onTouchStart={e => { y.current = e.touches[0].clientY; }}
      onTouchEnd={e => { if (y.current != null && e.changedTouches[0].clientY - y.current > 40) onClose(); y.current = null; }}>
      <span className="ss-drawer-grip" aria-hidden="true" />
      <div className="ss-drawer-bar">
        <Segmented size="sm" block value={tab} onChange={setTab} options={tabs.map(t => ({ ...t, label: t.value === "supplies" ? "What you need" : t.label }))} label="Class panel" />
        <IconBtn label="Close panel" icon={<X size={18} />} size={40} onClick={onClose} />
      </div>
    </div>
    <div className={`ss-drawer-body is-${tab}`}>{children}</div>
  </div>;
}

/* ═══════════════════════════ Free YouTube live ═══════════════════════════ */

function YouTubeSession({ s }: { s: Session }) {
  const now = useNow(1000);
  const st = sessionState(s, now);
  const over = st === "replay" || st === "processing";
  return <PortalShell active="classes" back={{ to: P.classes, label: "Classes" }} kicker="Free live on YouTube" title={s.title} subtitle={<LocalTime date={s.start} withDay />}>
    <Card tone="ink" pad="lg" className="ss-yt">
      <div className="ss-yt-in">
        <Chip tone={st === "live" ? "live" : "outline"}>{st === "live" ? "Live now" : over ? "Watch the recording" : st === "tonight" ? "Tonight" : "Upcoming"}</Chip>
        <p className="ss-yt-b">{s.blurb}</p>
        {!over && st !== "live" && <CountdownBoxes to={s.start} tone="onDark" />}
        <div className="ss-wait-act">
          <Btn href={s.url} variant={st === "live" ? "live" : "pop"} iconEnd={<ExternalLink size={16} aria-hidden="true" />}>{st === "live" ? "Watch free on YouTube" : over ? "Watch on YouTube" : "Open on YouTube"}</Btn>
          {!over && <AddToCalendar session={s} onDark />}
        </div>
      </div>
    </Card>
    <p className="ss-proto">Free lives are open to everyone on YouTube. Club classes (with replays, chapters and the chat) are for Members.</p>
  </PortalShell>;
}
